import type { CSSProperties, ReactNode } from "react";
import { theme } from "../theme";

// Read-only renderer for the Markdown the document generator produces (and the reference
// library's converted publications): headings (six levels, inline formatting inside them),
// paragraphs, bullet / numbered lists, pipe tables (RoPA, retention schedules, deadline
// tables), "> Review required:" callouts, horizontal rules and **bold** / *italic* /
// `code` inline. Mirrors what services/docxGenerator.ts renders into Word so the
// on-screen preview matches the download. Deliberately not a full Markdown parser.

interface Props {
  content: string;
  /** Base font size in px; headings scale from it. */
  size?: number;
}

const ESCAPED_PIPE = "";

function isTableLine(line: string): boolean {
  const t = line.trim();
  return t.startsWith("|") && t.endsWith("|") && t.length > 2;
}

function isTableSeparator(line: string): boolean {
  return /^\|(\s*:?-{3,}:?\s*\|)+$/.test(line.trim());
}

function splitTableRow(line: string): string[] {
  return line.trim().slice(1, -1).replace(/\\\|/g, ESCAPED_PIPE).split("|")
    .map((c) => c.replace(new RegExp(ESCAPED_PIPE, "g"), "|").trim());
}

function inline(text: string): ReactNode[] {
  const parts = text.split(/(\*\*.*?\*\*|\*.*?\*|`.*?`)/);
  return parts.filter(Boolean).map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) return <em key={i}>{part.slice(1, -1)}</em>;
    if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
      return <code key={i} style={{ fontFamily: "monospace", fontSize: "0.92em", background: theme.color.panelAlt, padding: "0 4px", borderRadius: 3 }}>{part.slice(1, -1)}</code>;
    }
    return <span key={i}>{part}</span>;
  });
}

const cellStyle: CSSProperties = {
  border: `1px solid ${theme.color.border}`,
  padding: "5px 8px",
  verticalAlign: "top",
  textAlign: "left",
};

export function MarkdownDocument({ content, size = 12.5 }: Props) {
  const lines = content.replace(/\r\n/g, "\n").split("\n");
  const out: ReactNode[] = [];
  let listItems: ReactNode[] = [];
  let listOrdered = false;

  const flushList = (key: string) => {
    if (listItems.length === 0) return;
    const items = listItems;
    listItems = [];
    out.push(listOrdered
      ? <ol key={key} style={{ margin: "4px 0 8px", paddingLeft: 22 }}>{items}</ol>
      : <ul key={key} style={{ margin: "4px 0 8px", paddingLeft: 22 }}>{items}</ul>);
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const t = line.trim();

    if (isTableLine(line)) {
      flushList(`l${i}`);
      const block: string[] = [];
      while (i < lines.length && isTableLine(lines[i])) { block.push(lines[i]); i++; }
      i--;
      const rows = block.filter((l) => !isTableSeparator(l)).map(splitTableRow);
      if (rows.length === 0) continue;
      const [head, ...body] = rows;
      const cols = Math.max(...rows.map((r) => r.length));
      out.push(
        <div key={`t${i}`} style={{ overflowX: "auto", margin: "8px 0 12px" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: size - 0.5 }}>
            <thead>
              <tr>
                {Array.from({ length: cols }, (_, j) => (
                  <th key={j} style={{ ...cellStyle, borderBottom: `1px solid ${theme.color.line}`, background: theme.color.panelAlt, color: theme.color.heading, fontWeight: 700 }}>{inline(head[j] ?? "")}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((r, ri) => (
                <tr key={ri}>
                  {Array.from({ length: cols }, (_, j) => <td key={j} style={cellStyle}>{inline(r[j] ?? "")}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    const bullet = /^[-*]\s+(.*)/.exec(t);
    const numbered = /^\d+\.\s+(.*)/.exec(t);
    if (bullet || numbered) {
      const ordered = Boolean(numbered);
      if (listItems.length > 0 && ordered !== listOrdered) flushList(`l${i}`);
      listOrdered = ordered;
      listItems.push(<li key={i} style={{ marginBottom: 2 }}>{inline((bullet ?? numbered)![1])}</li>);
      continue;
    }
    flushList(`l${i}`);

    if (t.startsWith("# ")) {
      out.push(<h2 key={i} style={{ fontSize: size + 4, fontWeight: 700, margin: "16px 0 6px", color: theme.color.heading }}>{inline(t.slice(2))}</h2>);
    } else if (t.startsWith("## ")) {
      out.push(<h3 key={i} style={{ fontSize: size + 2, fontWeight: 700, margin: "14px 0 4px", color: theme.color.heading }}>{inline(t.slice(3))}</h3>);
    } else if (t.startsWith("### ")) {
      out.push(<h4 key={i} style={{ fontSize: size + 0.5, fontWeight: 600, margin: "10px 0 4px", color: theme.color.accentText }}>{inline(t.slice(4))}</h4>);
    } else if (/^#{4,6}\s/.test(t)) {
      // Deeper levels (common in converted publications) read as a minor heading, not as text with hashes.
      out.push(<h5 key={i} style={{ fontSize: size, fontWeight: 600, margin: "8px 0 2px", color: theme.color.heading }}>{inline(t.replace(/^#{4,6}\s+/, ""))}</h5>);
    } else if (t.startsWith(">")) {
      out.push(
        <div key={i} style={{ margin: "6px 0 10px", padding: "7px 10px", borderLeft: `3px solid ${theme.color.warning}`, background: "var(--c-warningBg)", borderRadius: 4, color: theme.color.text }}>
          {inline(t.replace(/^>\s?/, ""))}
        </div>,
      );
    } else if (t === "---" || t === "***") {
      out.push(<hr key={i} style={{ border: 0, borderTop: `1px solid ${theme.color.border}`, margin: "10px 0" }} />);
    } else if (!t) {
      out.push(<div key={i} style={{ height: 6 }} />);
    } else {
      out.push(<p key={i} style={{ margin: "0 0 6px" }}>{inline(t)}</p>);
    }
  }
  flushList("l-end");

  return <div style={{ fontSize: size, lineHeight: 1.7, color: theme.color.text }}>{out}</div>;
}
